import { DeliveryOperationRequest } from 'src/entities/DeliveryOperationRequest.entity';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

export type SelectedCycleIndex = { deliveryId: number; cycleIndexes: number[] };
export type DeliveryOperationsRequest = DeliveryOperationRequest;

/**
 * 選んだ行から「どの勤務のどの回転を対象にするか」を作る。
 *
 * /map は空配列を送っていたため、2 回転目だけを選んでもサーバへ伝わらなかった。
 * V3Plans は selectedCycleIndexes を同じ形で送る（hooks/usePlanningRequest.ts:164）。
 *
 * 回転追加した行はまだサーバに無く deliveryId が負のダミーなので、実在の delivery に
 * 載せ替える。V3Plans は空の回転を delivery.cycles に足すため delivery が自明で、
 * この付け替えが要らない。載せ先は「同時に選ばれている勤務 → そのカードの勤務 →
 * 同じ車両の delivery」の順で決める（選んでいない人の勤務に回転を足さないため）。
 */
export function buildSelectedCycleIndexes(
  selectedRowIds: ReadonlySet<string>,
  rowGroups: readonly VehicleRowGroup[],
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
): SelectedCycleIndex[] {
  const known = new Set(deliveries.map((d) => d.id));
  const deliveryIdByTruck = new Map(deliveries.map((d) => [d.truckId, d.id]));
  const byDelivery = new Map<number, { rotationNumber: number; isPlaceholder: boolean }[]>();

  rowGroups.forEach((group) => {
    const selectedRows = group.rows.filter((r) => selectedRowIds.has(r.rowId));
    const selectedRealDeliveryId = selectedRows
      .find((r) => !r.source.isPlaceholderRow && known.has(r.source.deliveryId))
      ?.source.deliveryId;

    selectedRows.forEach((r) => {
      const deliveryId = r.source.isPlaceholderRow
        ? selectedRealDeliveryId ?? group.shiftTruckId ?? deliveryIdByTruck.get(r.truckId)
        : r.source.deliveryId;
      if (deliveryId == null || !known.has(deliveryId)) return;
      const rows = byDelivery.get(deliveryId) ?? [];
      if (!rows.some((it) => it.rotationNumber === r.rotationNumber)) {
        rows.push({ rotationNumber: r.rotationNumber, isPlaceholder: r.source.isPlaceholderRow });
      }
      byDelivery.set(deliveryId, rows);
    });
  });

  return [...byDelivery].map(([deliveryId, rows]) => {
    // 割り当て済みが含まれるなら割り当て済みだけ、すべて未割り当てなら最初の回転だけ。
    // V3Plans hooks/usePlanningRequest.ts:106-122 と同じ絞り込み
    const allocated = rows.filter((it) => !it.isPlaceholder);
    const cycleIndexes = allocated.length > 0
      ? allocated.map((it) => it.rotationNumber)
      : [rows[0].rotationNumber];
    return { deliveryId, cycleIndexes };
  });
}

/**
 * 対象 delivery の現在の運行順。サーバはこれを基準に組み替える。
 * V3Plans (hooks/usePlanningRequest.ts:88-104) と同じ 4 フィールドを送る。
 *
 * 対象外の delivery を混ぜると、選んでいない車両の計画まで組み替わる。
 */
export function buildDeliveryOperations(
  deliveryIds: readonly number[],
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
): DeliveryOperationsRequest[] {
  const byId = new Map(deliveries.map((d) => [d.id, d]));

  return deliveryIds.flatMap((deliveryId) => {
    const delivery = byId.get(deliveryId);
    if (!delivery) return [];
    return [{
      deliveryId,
      operations: (delivery.operations ?? []).map((op) => ({
        id: op.id,
        cycle: op.cycle,
        // API の order_id は leg_run.id（shipment_order_leg_run_operation.rb:245）。
        // /map が読む operation には orderId が無く shipmentOrderLegRunId が同じ値を指す
        orderId: op.shipmentOrderLegRunId,
        action: op.action,
      })),
    }];
  });
}
